type PointsConfig = {
  [key: string]: number;
};

export class CurveAnalyser {
  private curveCoordinates: [number, number][];
  private _curveMap: Map<number, number> | null = null;
  private _xValues: number[] | null = null; // Changed from _spendValues

  constructor(curveCoordinates: [number, number][]) {
    this.curveCoordinates = curveCoordinates;
  }

  private get curveMap(): Map<number, number> {
    if (!this._curveMap) {
      this._curveMap = new Map(this.curveCoordinates.map(([x, y]) => [x, y]));
    }
    return this._curveMap;
  }

  private get xValues(): number[] {
    if (!this._xValues) {
      this._xValues = Array.from(this.curveMap.keys());
    }
    return this._xValues;
  }

  private findExactMatch(x: number): [number, number] | null {
    const y = this.curveMap.get(x);
    return y !== undefined ? [x, y] : null;
  }

  private findClosestX(targetX: number): number | null {
    if (this.xValues.length === 0) return null;

    return this.xValues.reduce((prev, curr) => {
      return Math.abs(curr - targetX) < Math.abs(prev - targetX) ? curr : prev;
    });
  }

  private findClosestMatch(x: number): [number, number] | null {
    const closestX = this.findClosestX(x);
    if (!closestX) return null;

    return this.findExactMatch(closestX);
  }

  public mapTargetPointsToCoordinates(
    pointsConfig: PointsConfig
  ): Partial<Record<string | number, [number, number]>> {
    return Object.entries(pointsConfig).reduce(
      (acc, [key, targetX]) => {
        // Changed from targetSpend
        const match = this.findExactMatch(targetX) || this.findClosestMatch(targetX);

        if (match) {
          acc[key as keyof typeof pointsConfig] = match;
        }

        return acc;
      },
      {} as Partial<Record<keyof typeof pointsConfig, [number, number]>>
    );
  }

  /**
   * Applies a custom function to each pair of x and y coordinates
   * and collects the results.
   * @param handler A function that takes x and y and returns a result
   * @returns The result from applying the handler to each coordinate pair
   */
  public applyToCoordinates<T>(handler: (x: number, y: number) => T): T {
    return this.curveCoordinates.reduce((acc, [x, y]) => handler(x, y), undefined as any);
  }

  /**
   * Finds an interpolated y value for a given x using linear interpolation.
   * @param x The x value for which to find the interpolated y value.
   * @returns The interpolated y value or null if x is out of bounds.
   */
  public interpolate(x: number): number | null {
    const xValues = this.xValues;
    if (!xValues || xValues.length < 2) return null;

    const exactMatch = this.findExactMatch(x);
    if (exactMatch) {
      return exactMatch[1];
    }

    // Find the two x values between which x lies
    let lowerIndex = -1;
    for (let i = 0; i < xValues.length - 1; i++) {
      const prevX = xValues[i];
      const nextX = xValues[i + 1];
      if (!prevX || !nextX) continue;
      if (prevX <= x && x < nextX) {
        lowerIndex = i;
        break;
      }
    }

    if (lowerIndex === -1) return null; // x is out of bounds

    const x1 = xValues[lowerIndex];
    const x2 = xValues[lowerIndex + 1];
    if (!x1 || !x2) return null;
    const y1 = this.curveMap.get(x1)!;
    const y2 = this.curveMap.get(x2)!;

    // Linear interpolation formula
    const y = y1 + ((y2 - y1) * (x - x1)) / (x2 - x1);
    return y;
  }
}
